import React, { useState } from 'react';
import classNames from 'classnames';
import { Field, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import validations from 'validations';
import MapField from '../../../../components/MapField';
import ListingSlugField from 'router/subrouters/ListingCreationWizard/components/ListingSlugField';
import TextareaArrayField from 'components/TextareaArrayField';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  KEY_ASSETS_MAX_CHARACTERS,
  MIN_REQUIRED_FIELD_ITEMS,
  PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
} from 'router/subrouters/ListingCreationWizard/utils';
import { Listing } from 'models/Listing';
import { Button } from 'ncoded-component-library';
import SelectField from 'components/SelectField';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useFirstStepValues from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/components/FirstStep/hooks';
import ListingContactColleagues from 'router/subrouters/ListingCreationWizard/common-pages/ListingContactColleagues';

import './BusinessInfo.styles.scss';
import './BusinessInfo.styles.responsive.scss';

type BusinessInfoProps = {
  className?: string;
};

const BusinessInfo: React.FC<BusinessInfoProps> = () => {
  const { t } = useTranslation();
  const {
    values: { location },
  } = useFormState<Listing>();
  const { dealLocationOptions } = useFirstStepValues();
  const { change } = useForm();

  const [showPreciseLocation, setShowPreciseLocation] = useState(!!location);

  return (
    <div className="lcw-page">
      <p className="lcw-page__heading__title">{t('businessListingInfo')}</p>

      <div className="lcw-custom-label-container">
        <h3>{t('keyListingInformation')}</h3>
      </div>

      <div>
        <Field
          name="dealLocation"
          required
          searchable
          className="location-select"
          label={t('location')}
          component={SelectField}
          options={dealLocationOptions}
          validate={validations.required()}
        />

        <Button
          variant="link"
          iconPosition="right"
          icon={ChevrondownIcon}
          className={classNames('lcw-add-more-location', {
            up: showPreciseLocation,
          })}
          onClick={() => {
            if (showPreciseLocation) {
              change('location', null);
            }
            setShowPreciseLocation((prev) => !prev);
          }}
        >
          {t(
            showPreciseLocation
              ? 'hideMoreSpecificLocation'
              : 'addMoreSpecificLocation',
          )}
        </Button>
      </div>

      {showPreciseLocation && (
        <Field
          description={t('locationDescription')}
          name="location"
          component={MapField}
        />
      )}

      <Field
        label={t('listingTitle')}
        name="listingTitle"
        component={InputField}
        validate={validations.required()}
        placeholder={t('enterListingTitle')}
        required
        maxLength={TITLE_MAX_CHARACTERS}
      />
      <ListingSlugField />
      <Field
        label={t('shortDescription')}
        name="shortDescription"
        component={InputField}
        validate={validations.required()}
        placeholder={t('shortDescriptionPlaceholder')}
        maxLength={200}
        required
      />
      <Field
        className="lcw-description-field-textarea"
        label={t('fullDescription')}
        required
        name="businessDescription"
        component={TextAreaField}
        validate={validations.required()}
        placeholder={t('thisWillApearProminently')}
        maxLength={DESCRIPTION_MAX_CHARACTERS}
      />

      <div className="lcw-custom-label-container">
        <h3>{t('businessHighlights')}</h3>
        <p>{t('businessHighlightsDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="highlights"
        fieldLabel={t('highlight')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={HIGHLIGHTS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('products/Services')}</h3>
        <p>{t('products/ServicesDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="products"
        fieldLabel={t('products/Services')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={PRODUCTS_AND_SERVICES_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('keyAssets')}</h3>
        <p>{t('keyAssetsDescMessage')}</p>
      </div>
      <TextareaArrayField
        name="keyAssets"
        fieldLabel={t('keyAsset')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={KEY_ASSETS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_FIELD_ITEMS}
      />

      <ListingContactColleagues />
    </div>
  );
};

export default BusinessInfo;
